import { Inject, Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, switchMap } from 'rxjs';
import {
  LoggingLevelEnum,
  LoggingService as AuthLoggingService,
  SessionService as AuthSessionService,
  IAppState,
  AUTH_CONFIG,
  IAuthConfig,
  AuthService
} from '@auth/auth';
import { UserAttributes } from '@auth/auth/lib/models/user';
import { v4 as uuidv4 } from 'uuid';
import { User } from '../../classes/user/user';
import { ShellService } from '../shell.service';
import { EventType } from '../../enums/enums/event-type';
import { UserSwService } from '../userSw/user-sw.service';

const ErrorCallsites = require('error-callsites');

@Injectable({
  providedIn: 'root'
})
export class ShellLoggingService extends AuthSessionService<IAppState> {
  private readonly shellUser$ = new ReplaySubject<User>();
  private sessionId!: string;
  record: any = {};

  constructor(
    authLoggingService: AuthLoggingService,
    authService: AuthService<IAppState>,
    @Inject(AUTH_CONFIG) authConfig: IAuthConfig,
    @Inject('ShellService') private shellService: ShellService,
    private userSw: UserSwService
  ) {
    super(authConfig, authLoggingService, authService);
  }

  changeShellUser(user: {
    userAttributes: Record<string, UserAttributes[]>;
  }): void {
    this.shellService.changeUserAttributes(user);
    this.shellUser$.next(new User(user));
  }

  getUser(): Observable<User> {
    return navigator.serviceWorker
      ? this.userSw
          .getUser()
          .pipe(switchMap((user) => (user ? of(user) : this.shellUser$)))
      : this.shellUser$;
  }

  log(
    eventType: EventType,
    message: string,
    eventData?: any,
    error?: Error
  ): Observable<any> {
    const callsite = this.getCallsite(error);

    if (eventType == EventType.Successful_Login) {
      if (eventData) {
        eventData['viewPortWidth'] = window.innerWidth;
      } else {
        eventData = { viewPortWidth: window.innerWidth };
      }
    }

    this.record = {
      loggingUnit: this.sessionId,
      topLevelName: `SHELL-${LoggingLevelEnum.EVENT}`,
      middleLevelName: callsite.getTypeName() ?? 'Unknown Class',
      bottomLevelName: callsite.getMethodName() ?? 'Unknown Method',
      message: message,
      machine: 'User Machine',
      eventTypeId: eventType,
      eventData: eventData ? JSON.stringify(eventData) : null,
      pageTitle: 'Broadspire Claims',
      applicationPath: window.location.href.substring(0, 5000),
      fileName: callsite.getFileName(),
      lineNumber: callsite.getLineNumber() || 0
    };

    return this.logToDatabase(this.record);
  }

  getSessionId(): string {
    if (!this.sessionId) {
      this.sessionId = uuidv4();
    }

    return this.sessionId;
  }

  private getCallsite(original?: Error) {
    if (original) {
      const callsites = ErrorCallsites(original);
      if (callsites) {
        return callsites[0];
      }
    }

    return {
      getTypeName: () => 'Unknown Class',
      getMethodName: () => 'Unknown Method',
      getFileName: () => 'Unknown File',
      getLineNumber: () => 0,
      getColumnNumber: () => 0,
      getFunctionName: () => 'Unknown Source'
    };
  }
}
